import type {
	ICellValue,
	ICurrencyField,
	InternalState,
	INumberField,
	IPercentField,
} from '../types'

import { isNumber } from 'lodash/fp'
import z from 'zod'

import { str2number } from '../convert'
import { BasicValueType, FieldType, FOperator } from '../types'
import { Field } from './field'

export type ICommonNumberField = INumberField | ICurrencyField | IPercentField
/* | IRatingField
	
	| IAutoNumberField */
export const commonNumberFields = new Set([
	FieldType.Number,
	FieldType.Currency,
	FieldType.Percent /*
	FieldType.Rating,
	FieldType.AutoNumber,
	FieldType.Formula, */,
])

export abstract class NumberBaseField extends Field {
	constructor(
		public override field: ICommonNumberField,
		state: InternalState
	) {
		super(field, state)
	}
	get openValueSchema() {
		return z.number().nullable()
	}

	static readonly _acceptFilterOperators = [
		FOperator.Is,
		FOperator.IsNot,
		FOperator.IsGreater,
		FOperator.IsGreaterEqual,
		FOperator.IsLess,
		FOperator.IsLessEqual,
		FOperator.IsEmpty,
		FOperator.IsNotEmpty,
		FOperator.NumberBetween,
	]

	static readonly FOperatorDescMap = {
		[FOperator.Is]: '=',
		[FOperator.IsNot]: '≠',
		[FOperator.IsGreater]: '>',
		[FOperator.IsGreaterEqual]: '≥',
		[FOperator.IsLess]: '<',
		[FOperator.IsLessEqual]: '≤',
		[FOperator.IsEmpty]: 'is_empty',
		[FOperator.IsNotEmpty]: 'is_not_empty',
		[FOperator.NumberBetween]: 'num_between',
	}

	static readonly cellValueSchema = z.number().nullable()

	static readonly openWriteValueSchema = z.number().nullable()

	override showFOperatorDesc(type: FOperator) {
		return NumberBaseField.FOperatorDescMap[
			type as keyof typeof NumberBaseField.FOperatorDescMap
		]
	}

	get acceptFilterOperators(): FOperator[] {
		return NumberBaseField._acceptFilterOperators
	}
	get basicValueType(): BasicValueType {
		return BasicValueType.Number
	}

	cellValueToString(_cellValue: ICellValue): string | null {
		return null
	}
	coerceValue(value: any): number | null {
		if (typeof value === 'number') {
			return value
		}
		if (typeof value === 'string') {
			return str2number(value)
		}

		return null
	}
	stdValueToCellValue(stdValue: string): ICellValue {
		return stdValue == null ? null : str2number(stdValue)
	}
	cellValueToApiStandardValue(cellValue: ICellValue): ICellValue {
		return cellValue
	}

	cellValueToApiStringValue(cellValue: ICellValue): string | null {
		return this.cellValueToString(cellValue)
	}

	cellValueToOpenValue(cellValue: ICellValue): number | null {
		return cellValue as number
	}

	openWriteValueToCellValue(openWriteValue: number | null) {
		return openWriteValue
	}

	validateCellValue(cellValue: ICellValue) {
		return NumberBaseField.cellValueSchema.safeParse(cellValue)
	}

	validateOpenWriteValue(owv: number | null) {
		return NumberBaseField.openWriteValueSchema.safeParse(owv)
	}
	validate(value: any): value is number {
		return isNumber(value) && !Number.isNaN(value)
	}
	defaultValueForCondition(condition: any): number | null {
		if (condition.operator !== FOperator.Is) {
			return null
		}

		const { value } = condition
		if (value && value.length && value[0]) {
			return str2number(value[0])
		}

		return null
	}
}
